import {
  downloadToCSV,
  getTableColumns,
  ITripTableProps,
  TimeLineTable,
  useTimer,
  useTranslation
} from '@fareye/common'
import { AllIssueListing } from '@fareye/issue-resolution'
import { Flex, RenderControl } from '@fareye/ui'
import {
  camelCase,
  cancelToken,
  currenthhmmA,
  dMmm,
  flatten,
  get,
  getQueryParams,
  getXlsx,
  isEmpty,
  responseParser,
  serialzeAggregationsToSummaryData,
  snakeCase
} from '@fareye/utils'
import queryString from 'query-string'
import React, { useRef } from 'react'

const Flagging = React.lazy(() => import('../../../components/Flagging/'))

const initApiResponse = {
  data: [],
  isLoading: false,
  isError: false,
  isSuccess: false,
  message: '',
  meta: { totalElements: 0 }
}

const ConsignerTable = React.memo((props: ITripTableProps) => {
  let {
    // data,
    baseUrl,
    timer,
    match,
    config,
    mapping,
    endDate,
    fileName,
    location,
    startDate,
    setLastUpdate,
    searchData,
    selectedKey,
    searchQuery,
    setSummaryData,
    refreshableTable = true
  } = props
  const sortableColumns = React.useRef<any>([])
  // TODO : Columns to be passed only from unique prop
  const [downloadLoading, setDownloadLoading] = React.useState<boolean>(false)
  const [refresh, setRefresh] = React.useState<boolean>(false)
  const [tripData, setApiResponse] = React.useState<any>(initApiResponse)
  const [sorting, setSortBy] = React.useState({
    sortBy: '',
    sortByDirection: true
  })
  const [pageNo, setPageNo] = React.useState(0)
  const [recordsPerPage, setRecordsPerPage] = React.useState(10)
  const query = queryString.parse(location.search)
  const [changeDetect, setChangeDetect] = React.useState(false)
  const [showCommentModule, setShowCommentModule] = React.useState(false)
  const [toggleFlaggingModal, setToggleFlaggingModal] = React.useState(false)
  // FIX ME ROUTE REGISTER USING PARAMS SEPCIFIC TO USAGE slug, transporter, route
  const { id }: any = match?.params || {}

  const idParam = id ? decodeURIComponent(id).split('-to-') : []
  const params = id?.includes('-to-')
    ? { origin: idParam[0], destination: idParam[1] }
    : id
    ? { transporter: id }
    : {}

  let masterData = tripData.data

  const { style, className, variant } = config.styling
  const {
    links,
    apiConfig,
    flagging,
    downloadApiConfig,
    issueRaisedConfig
  } = config.config
  const { driverFlagging, vehicleFlagging } = flagging || {}

  const { sortBy, sortByDirection } = sorting
  const searchParams = {
    ...query,
    startDate,
    endDate,
    ...params,
    pageNo: pageNo + 1,
    plant: props.plant ? props.plant : '',
    recordsPerPage,
    sortBy: snakeCase(sortBy),
    sortByDirection: sortBy ? (sortByDirection ? 'desc' : 'asc') : '',
    ...searchQuery,
    ...apiConfig?.queryParams
  }
  const queryParam = getQueryParams(searchParams)
  const isFirstRun = useRef(false)
  React.useEffect(() => {
    if (isFirstRun.current) {
      setPageNo(0)
      setRecordsPerPage(10)
      setApiResponse(initApiResponse)
      setChangeDetect(c => !c)
    }
    isFirstRun.current = true
  }, [searchQuery, selectedKey, startDate, endDate])

  React.useEffect(() => {
    apiConfig?.apiURL && fetchData()
  }, [pageNo, recordsPerPage, changeDetect, sorting, timer])

  if (!Array.isArray(masterData)) {
    masterData = flatten(Object.values(masterData))
    masterData = masterData.filter((obj: any) =>
      Object.values(obj).some((val: any) => {
        return typeof val === 'string'
          ? val.toLowerCase().includes(searchData.toLowerCase())
          : false
      })
    )
  }

  React.useEffect(() => {
    if (isFirstRun.current && refresh) fetchData()
  }, [refresh])

  const subComponentConfig = config.children
  const sourceRef = useRef<any>(null)
  const requestIdRef = useRef(0)
  const sourceDownload = cancelToken().source()

  React.useEffect(() => {
    return () => {
      sourceRef.current?.cancel()
    }
  }, [])
  const tableLinks: any = { ...links }
  const column: string[] =
    selectedKey === 'tabular'
      ? config.config.columnsList
      : config.config.columns
  const { t, i18n } = useTranslation()
  const selectedColumns = getTableColumns(column, {
    t,
    i18n,
    links: tableLinks,
    queryParams: {
      tripShare: props.plant ? `?plant=${props.plant}` : undefined
    }
  }).map((column: any) => ({
    ...column,
    sortable: sortableColumns?.current.includes(column.id)
  }))

  async function fetchData() {
    sourceRef.current?.cancel()
    const newSource = cancelToken().source()
    sourceRef.current = newSource
    const requestId = ++requestIdRef.current
    try {
      setApiResponse((prev: any) => ({ ...prev, isLoading: true }))
      const {
        data: {
          content,
          contentSummary,
          meta
        }
      } = await get(
        `${apiConfig?.apiURL}?${queryParam}`,
        newSource.token,
        {
          headers: { 'Key-Inflection': 'camel', 'X-Change-Case': false }
        }
      )
      if (requestId !== requestIdRef.current) return
      sortableColumns.current = (meta?.sortableAttributes || []).map(
        (column: string) => camelCase(column)
      )
      setSummaryData?.(contentSummary ? serialzeAggregationsToSummaryData(contentSummary) : {})
      setApiResponse((prev: any) => {
        const tempData = [...prev.data]
        tempData.splice(pageNo * recordsPerPage, recordsPerPage, ...content)
        return {
          data: tempData,
          isError: false,
          isSuccess: true,
          isLoading: false,
          meta,
          message: isEmpty(content) ? apiConfig?.apiDataState?.noDataMessage : ''
        }
      })
      setRefresh(false)
      setLastUpdate(currenthhmmA())
    } catch (err: any) {
      if (requestId !== requestIdRef.current) return
      const { message, error } = responseParser(err.response)
      setApiResponse((prev: any) => ({
        ...prev,
        isSuccess: false,
        isError: true,
        isLoading: false,
        message
      }))
      console.error('App Error:', error)
    }
  }

  async function downloadCSV() {
    setDownloadLoading(true)
    delete searchParams.pageNo
    delete searchParams.recordsPerPage
    const queryParam = getQueryParams({
      ...searchParams,
      ...downloadApiConfig.queryParams,
      plant: props.plant ? props.plant : ''
    })
    try {
      const { data } = await getXlsx(
        `${downloadApiConfig?.apiURL}?${queryParam}`,
        sourceDownload.token
      )
      downloadToCSV({
        data,
        fileName: fileName || `${dMmm()}`,
        type: 'text/zip',
        extension: 'zip'
      })
    } finally {
      setDownloadLoading(false)
    }
  }

  return (
    <RenderControl
      data={masterData}
      isLoading={tripData.isLoading}
      isSuccess={!isEmpty(masterData)}
      message={tripData.message}
      isError={tripData.isError}
      variant={'xl'}
    >
      <Flex column>
        <div style={{ paddingTop: 10, paddingBottom: 10, ...style }}>
          <TimeLineTable
            key={selectedKey}
            className={`${
              selectedKey === 'tabular' ? 'striped' : ''
            } ${className} responsive`}
            data={masterData}
            columns={selectedColumns}
            defaultPageSize={recordsPerPage}
            totalElements={tripData?.meta?.totalElements || masterData.length}
            toggleFlagging={
              flagging ? () => setToggleFlaggingModal(true) : undefined
            }
            size="s"
            sorting={sorting}
            sortable={selectedKey === 'tabular'}
            showTotalCount={false}
            refresh={setRefresh}
            refreshing={refresh}
            commentingModule={setShowCommentModule}
            downloadCSV={downloadCSV}
            downloadLoading={downloadLoading}
            showPagination={true}
            defaultPageIndex={pageNo}
            onPageChange={(pageIndex: number) => setPageNo(pageIndex)}
            onPageSizeChange={(pageSize: number) => {
              setPageNo(0)
              setRecordsPerPage(pageSize)
              setApiResponse(initApiResponse)
            }}
            subComponent={
              selectedKey !== 'tabular'
                ? ({ original, row }: any) => {
                    if (subComponentConfig) {
                      return Object.keys(subComponentConfig).map(
                        (subComp: any, i: number) => {
                          const Comp: any =
                            mapping?.[subComponentConfig[subComp].component]
                          return (
                            <Comp
                              key={`sub-component-${i}`}
                              baseUrl={baseUrl}
                              etaInfo={
                                original.etaInfo.length > 0
                                  ? original.etaInfo
                                  : false
                              }
                              timelineData={original}
                            />
                          )
                        }
                      )
                    }
                  }
                : undefined
            }
            onSortedChange={(ev: any) => {
              setSortBy({
                sortBy: ev[0].id,
                sortByDirection:
                  ev[0].id === sorting.sortBy
                    ? !sorting.sortByDirection
                    : ev[0].desc
              })
              setPageNo(0)
              setApiResponse(initApiResponse)
            }}
            issueLabel={issueRaisedConfig?.config?.issueLabel || "Issues Raised"}
          />
        </div>
      </Flex>
      {showCommentModule ? (
        <AllIssueListing
          showDrawer={showCommentModule}
          setShowDrawer={setShowCommentModule}
          resourceId={''}
          config={issueRaisedConfig}
          resourceType="ConsignerTrip"
          baseUrl={''}
          updateIssueCount={() => {}}
          startDate={startDate}
          endDate={endDate}
          t={t}
        />
      ) : null}
      {toggleFlaggingModal && (driverFlagging || vehicleFlagging) ? (
        <Flagging
          config={flagging}
          toggleFlaggingModal={toggleFlaggingModal}
          setToggleFlaggingModal={setToggleFlaggingModal}
        />
      ) : null}
    </RenderControl>
  )
})

const TripTable = (props: any) => {
  const { config, maxRefreshCount, refreshInterval } = props
  const { minAutoRefreshInterval } = config?.config || {}
  const [timer] = useTimer({
    maxRefreshCount,
    refreshInterval,
    minAutoRefreshInterval
  })
  return <ConsignerTable {...props} timer={timer} />
}

export default TripTable
